/**
 * Theme Color Utilities
 * Provides functions to generate color variations and CSS custom properties
 * based on the event's theme color
 */

/**
 * Converts hex color to RGB values
 * @param {string} hex - Hex color string (e.g., "#3B82F6")
 * @returns {object} RGB values {r, g, b}
 */
export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Converts RGB values to hex color
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color string
 */
export function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Lightens a color by a specified amount
 * @param {string} hex - Hex color string
 * @param {number} amount - Amount to lighten (0-1)
 * @returns {string} Lightened hex color
 */
export function lightenColor(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const { r, g, b } = rgb;
  const newR = Math.min(255, Math.round(r + (255 - r) * amount));
  const newG = Math.min(255, Math.round(g + (255 - g) * amount));
  const newB = Math.min(255, Math.round(b + (255 - b) * amount));

  return rgbToHex(newR, newG, newB);
}

/**
 * Darkens a color by a specified amount
 * @param {string} hex - Hex color string
 * @param {number} amount - Amount to darken (0-1)
 * @returns {string} Darkened hex color
 */
export function darkenColor(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const { r, g, b } = rgb;
  const newR = Math.max(0, Math.round(r * (1 - amount)));
  const newG = Math.max(0, Math.round(g * (1 - amount)));
  const newB = Math.max(0, Math.round(b * (1 - amount)));

  return rgbToHex(newR, newG, newB);
}

/**
 * Generates a color palette based on the theme color
 * @param {string} themeColor - The base theme color
 * @returns {object} Color palette with various shades and variations
 */
export function generateColorPalette(themeColor) {
  if (!themeColor) {
    // Default fallback colors
    return {
      primary: "#3B82F6",
      primaryLight: "#60A5FA",
      primaryDark: "#1D4ED8",
      primaryLighter: "#DBEAFE",
      primaryDarker: "#1E3A8A",
      primaryMuted: "#93C5FD",
      primarySubtle: "#EFF6FF",
      primaryText: "#1E40AF",
      primaryBorder: "#BFDBFE",
      primaryHover: "#2563EB",
      primaryFocus: "#3B82F6",
      primaryDisabled: "#9CA3AF",
      primaryBackground: "#F8FAFC",
      primarySurface: "#FFFFFF",
      primarySurfaceHover: "#F1F5F9",
      primarySurfaceBorder: "#E2E8F0",
      primarySurfaceText: "#334155",
      primarySurfaceTextMuted: "#64748B",
      primarySurfaceTextSubtle: "#94A3B8",
      primarySuccess: "#10B981",
      primaryWarning: "#F59E0B",
      primaryError: "#EF4444",
      primaryInfo: "#3B82F6",
    };
  }

  return {
    primary: themeColor,
    primaryLight: lightenColor(themeColor, 0.2),
    primaryDark: darkenColor(themeColor, 0.2),
    primaryLighter: lightenColor(themeColor, 0.4),
    primaryDarker: darkenColor(themeColor, 0.4),
    primaryMuted: lightenColor(themeColor, 0.3),
    primarySubtle: lightenColor(themeColor, 0.45),
    primaryText: darkenColor(themeColor, 0.3),
    primaryBorder: lightenColor(themeColor, 0.35),
    primaryHover: darkenColor(themeColor, 0.1),
    primaryFocus: themeColor,
    primaryDisabled: "#9CA3AF",
    primaryBackground: lightenColor(themeColor, 0.48),
    primarySurface: "#FFFFFF",
    primarySurfaceHover: lightenColor(themeColor, 0.47),
    primarySurfaceBorder: lightenColor(themeColor, 0.4),
    primarySurfaceText: darkenColor(themeColor, 0.4),
    primarySurfaceTextMuted: darkenColor(themeColor, 0.2),
    primarySurfaceTextSubtle: lightenColor(themeColor, 0.2),
    primarySuccess: "#10B981",
    primaryWarning: "#F59E0B",
    primaryError: "#EF4444",
    primaryInfo: themeColor,
  };
}

/**
 * Applies theme colors as CSS custom properties to the document
 * @param {string} themeColor - The theme color to apply
 */
export function applyThemeColors(themeColor) {
  const palette = generateColorPalette(themeColor);
  const root = document.documentElement;

  Object.entries(palette).forEach(([key, value]) => {
    root.style.setProperty(
      `--color-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
      value
    );
  });
}

/**
 * Gets theme color CSS custom property
 * @param {string} colorName - The color name (e.g., 'primary', 'primary-light')
 * @returns {string} CSS custom property reference
 */
export function getThemeColor(colorName) {
  return `var(--color-${colorName.replace(/([A-Z])/g, "-$1").toLowerCase()})`;
}

/**
 * Generates Tailwind CSS classes based on theme colors
 * @param {string} themeColor - The theme color
 * @returns {object} Object with Tailwind-style color classes
 */
export function getThemeColorClasses(themeColor) {
  const palette = generateColorPalette(themeColor);

  return {
    // Primary colors
    primary: `bg-[${palette.primary}]`,
    primaryLight: `bg-[${palette.primaryLight}]`,
    primaryDark: `bg-[${palette.primaryDark}]`,
    primaryLighter: `bg-[${palette.primaryLighter}]`,
    primaryDarker: `bg-[${palette.primaryDarker}]`,
    primaryMuted: `bg-[${palette.primaryMuted}]`,
    primarySubtle: `bg-[${palette.primarySubtle}]`,

    // Text colors
    primaryText: `text-[${palette.primaryText}]`,
    primaryTextMuted: `text-[${palette.primarySurfaceTextMuted}]`,
    primaryTextSubtle: `text-[${palette.primarySurfaceTextSubtle}]`,

    // Border colors
    primaryBorder: `border-[${palette.primaryBorder}]`,
    primarySurfaceBorder: `border-[${palette.primarySurfaceBorder}]`,

    // Hover states
    primaryHover: `hover:bg-[${palette.primaryHover}]`,
    primarySurfaceHover: `hover:bg-[${palette.primarySurfaceHover}]`,

    // Focus states
    primaryFocus: `focus:ring-[${palette.primaryFocus}]`,

    // Background colors
    primaryBackground: `bg-[${palette.primaryBackground}]`,
    primarySurface: `bg-[${palette.primarySurface}]`,

    // Status colors
    primarySuccess: `bg-[${palette.primarySuccess}]`,
    primaryWarning: `bg-[${palette.primaryWarning}]`,
    primaryError: `bg-[${palette.primaryError}]`,
    primaryInfo: `bg-[${palette.primaryInfo}]`,
  };
}

/**
 * Gets inline styles for theme colors
 * @param {string} themeColor - The theme color
 * @returns {object} Object with inline style properties
 */
export function getThemeColorStyles(themeColor) {
  const palette = generateColorPalette(themeColor);

  return {
    primary: { backgroundColor: palette.primary },
    primaryLight: { backgroundColor: palette.primaryLight },
    primaryDark: { backgroundColor: palette.primaryDark },
    primaryLighter: { backgroundColor: palette.primaryLighter },
    primaryDarker: { backgroundColor: palette.primaryDarker },
    primaryMuted: { backgroundColor: palette.primaryMuted },
    primarySubtle: { backgroundColor: palette.primarySubtle },
    primaryText: { color: palette.primaryText },
    primaryTextMuted: { color: palette.primarySurfaceTextMuted },
    primaryTextSubtle: { color: palette.primarySurfaceTextSubtle },
    primaryBorder: { borderColor: palette.primaryBorder },
    primarySurfaceBorder: { borderColor: palette.primarySurfaceBorder },
    primaryHover: { backgroundColor: palette.primaryHover },
    primarySurfaceHover: { backgroundColor: palette.primarySurfaceHover },
    primaryFocus: { ringColor: palette.primaryFocus },
    primaryBackground: { backgroundColor: palette.primaryBackground },
    primarySurface: { backgroundColor: palette.primarySurface },
    primarySuccess: { backgroundColor: palette.primarySuccess },
    primaryWarning: { backgroundColor: palette.primaryWarning },
    primaryError: { backgroundColor: palette.primaryError },
    primaryInfo: { backgroundColor: palette.primaryInfo },
  };
}
